import React from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { makeAuthedRequest } from "shared/api/authRequest";
import { AdminTable } from "components/shared/AdminTable";
import { AdminTablePagination } from "components/shared/AdminTablePagination";
import { usePropertySites } from "./hooks/usePropertyData";
import { CATEGORY_LABEL, ImpactSummary, SiteSyncRecordImpact, statusColor, SyncTypesLabel } from "components/shared/siteSyncRecordShared";
import { Badge } from "components/ui/badge";
import { Switch } from "components/ui/switch";
import { Tooltip, TooltipContent, TooltipTrigger } from "components/ui/tooltip";
import { statusBadgeOutlineClass } from "shared/utils/statusChipColor";

const PROPERTY_SYNC_RECORDS_QUERY_KEY = "property-sync-records";

interface PropertySyncRecord {
  id: string;
  siteId?: string;
  timestamp: string;
  completedTimestamp?: string;
  requestedByUserId?: string;
  requestedByName?: string;
  triggerType: string;
  triggerName?: string;
  category: string;
  status: string;
  types: string;
  error?: string;
  impacts: SiteSyncRecordImpact[];
}

function useGetPropertySyncRecords(
  propertyExternalId: string,
  page: number,
  pageSize: number,
  includeRelatedSites: boolean,
) {
  return useQuery({
    queryKey: [PROPERTY_SYNC_RECORDS_QUERY_KEY, propertyExternalId, page, pageSize, includeRelatedSites],
    queryFn: () =>
      makeAuthedRequest<PropertySyncRecord[]>({
        method: "get",
        url: `/api/Property/${propertyExternalId}/sync-records`,
        params: { page, pageSize, includeRelatedSites },
      }),
    enabled: !!propertyExternalId,
  });
}

export default function PropertySyncRecords() {
  const params = useParams<{ externalId: string }>();
  const propertyExternalId = params.externalId!;
  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(50);
  const [includeRelatedSites, setIncludeRelatedSites] = React.useState(false);

  const { data, isLoading, isError, error } = useGetPropertySyncRecords(
    propertyExternalId,
    page,
    pageSize,
    includeRelatedSites,
  );
  const { data: sites } = usePropertySites(propertyExternalId);
  const records = data ?? [];

  const siteNameById = React.useMemo(() => {
    const map = new Map<string, string>();
    (sites ?? []).forEach((site) => {
      if (site.id) {
        map.set(site.id, site.name ?? site.id);
      }
    });
    return map;
  }, [sites]);

  const handleToggleRelatedSites = () => {
    setIncludeRelatedSites((prev) => !prev);
    setPage(0);
  };

  return (
    <AdminTable
      aria-label="Property sync records"
      toolbar={
        <div className="flex justify-end px-4 py-4">
          <div className="flex items-center gap-2">
            <Switch
              checked={includeRelatedSites}
              id="property-sync-include-related-sites"
              onCheckedChange={handleToggleRelatedSites}
            />
            <label
              className="text-sm font-medium text-slate-700"
              htmlFor="property-sync-include-related-sites"
            >
              Include related sites
            </label>
          </div>
        </div>
      }
      footer={
        isError ? undefined : (
          <AdminTablePagination
            currentPage={page}
            pageSize={pageSize}
            visibleRowCount={records.length}
            isLoading={isLoading}
            disableNext={records.length < pageSize}
            onNext={() => setPage((currentPage) => currentPage + 1)}
            onPrevious={() => setPage((currentPage) => Math.max(0, currentPage - 1))}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPage(0);
            }}
          />
        )
      }
    >
      {isLoading ? (
        <tbody>
          <tr>
            <td colSpan={includeRelatedSites ? 9 : 8}>
              <div className="flex items-center justify-center min-h-80 text-sm text-slate-500">
                Loading sync records...
              </div>
            </td>
          </tr>
        </tbody>
      ) : isError ? (
        <tbody>
          <tr>
            <td colSpan={includeRelatedSites ? 9 : 8}>
              <div className="flex items-center justify-center px-4 text-sm text-center min-h-80 text-rose-700">
                Failed to load sync records{error instanceof Error ? `: ${error.message}` : "."}
              </div>
            </td>
          </tr>
        </tbody>
      ) : (
        <>
          <thead>
            <tr>
              <th scope="col">Timestamp</th>
              <th scope="col">Completed</th>
              {includeRelatedSites ? <th scope="col">Site</th> : null}
              <th scope="col">Category</th>
              <th scope="col">Trigger</th>
              <th scope="col">Operations</th>
              <th scope="col">Status</th>
              <th scope="col">Impact</th>
              <th scope="col">Error</th>
            </tr>
          </thead>
          <tbody>
            {records.map((record) => (
              <tr key={record.id}>
                <td className="whitespace-nowrap">
                  {new Date(record.timestamp).toLocaleString()}
                  {record.requestedByName ? (
                    <div className="pt-1 text-[11px] text-slate-500">
                      {record.requestedByName}
                    </div>
                  ) : null}
                </td>
                <td className="whitespace-nowrap">
                  {record.completedTimestamp
                    ? new Date(record.completedTimestamp).toLocaleString()
                    : "—"}
                </td>
                {includeRelatedSites ? (
                  <td>
                    {record.siteId
                      ? siteNameById.get(record.siteId) ?? "Related site"
                      : "Property"}
                  </td>
                ) : null}
                <td>{CATEGORY_LABEL[record.category] ?? record.category}</td>
                <td>
                  {record.triggerName ? (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="cursor-default" tabIndex={0}>
                          {record.triggerType}
                        </span>
                      </TooltipTrigger>
                      <TooltipContent side="top">{record.triggerName}</TooltipContent>
                    </Tooltip>
                  ) : (
                    record.triggerType
                  )}
                </td>
                <td>
                  <SyncTypesLabel types={record.types} />
                </td>
                <td>
                  <Badge
                    className={statusBadgeOutlineClass(statusColor(record.status))}
                    variant="outline"
                  >
                    {record.status}
                  </Badge>
                </td>
                <td>
                  <ImpactSummary impacts={record.impacts} />
                </td>
                <td
                  className={`max-w-50 wrap-break-word text-xs ${record.error ? "text-rose-700" : ""}`}
                >
                  {record.error ?? "—"}
                </td>
              </tr>
            ))}
            {records.length === 0 ? (
              <tr>
                <td
                  colSpan={includeRelatedSites ? 9 : 8}
                  className="py-16 text-sm text-center text-slate-500"
                >
                  No sync records found.
                </td>
              </tr>
            ) : null}
          </tbody>
        </>
      )}
    </AdminTable>
  );
}
